export const SOLO_LEADER_TRIAL_DAYS = 7;
export const SOLO_LEADER_TRIAL_PROCESSED_MEETING_LIMIT = null;
export const SOLO_LEADER_PAID_PROCESSED_MEETING_LIMIT = 6;
export const SOLO_LEADER_PLAN_TYPE = "solo_leader";
export const COACH_ACTIVE_LEADER_PLAN_TYPE = "coach_active_leader";
export const COACH_LED_PROCESSED_MEETING_LIMIT = 6;
export const MANUAL_UNLIMITED_ACCESS_PROMO_CODE = "manual_unlimited_access";
export const PAYMENT_FAILURE_GRACE_DAYS = 7;
export const SOLO_LEADER_ANALYZED_STATUS_ALIASES = ["analyzed", "complete", "completed", "verified"] as const;
export const MEETING_PROCESSING_EVENT_TYPE = "processed_meeting_completed";
export const MEETING_PROCESSING_SOURCE_TRIAL = "trial";
export const MEETING_PROCESSING_SOURCE_INCLUDED = "included";
export const MEETING_PROCESSING_SOURCE_EXTRA_PACK = "extra_pack";
export const EXTRA_MEETING_PACK_QUANTITY = 5;
export const EXTRA_MEETING_PACK_PURCHASE_TYPE = "extra_meeting_pack";
export const EXTRA_MEETING_PACK_PURCHASER_TYPE = "solo_leader";
export const COACH_LEADER_EXTRA_MEETING_PACK_PURCHASE_TYPE = "coach_leader_extra_meeting_pack";
export const COACH_EXTRA_MEETING_PACK_PURCHASER_TYPE = "coach";
export const INTELLIGENCE_SEAT_PURCHASE_TYPE = "intelligence_seat";
export const INTELLIGENCE_PLUS_SEAT_PURCHASE_TYPE = "intelligence_plus_seat";
export const SOLO_INTELLIGENCE_SEAT_PURCHASE_TYPE = "solo_intelligence_seat";
export const SOLO_INTELLIGENCE_PLUS_SEAT_PURCHASE_TYPE = "solo_intelligence_plus_seat";
export const CAPTURE_HOUR_PACK_PURCHASE_TYPE = "capture_hour_pack";

export const ACTIVE_SOLO_SUBSCRIPTION_STATUSES = ["active", "trialing", "past_due"] as const;
export type ActiveSoloSubscriptionStatus = (typeof ACTIVE_SOLO_SUBSCRIPTION_STATUSES)[number];
export type SoloSubscriptionStatus = "free" | "trialing" | "active" | "past_due" | "canceled" | "unpaid" | "paused";
export type MeetingProcessingSource =
  | typeof MEETING_PROCESSING_SOURCE_TRIAL
  | typeof MEETING_PROCESSING_SOURCE_INCLUDED
  | typeof MEETING_PROCESSING_SOURCE_EXTRA_PACK;

export interface MeetingProcessingEventPayload {
  leader_id: string;
  meeting_id: string | null;
  transcript_upload_id: string;
  billing_owner_type: "solo_leader" | "coach" | "org";
  billing_owner_id: string | null;
  processing_period_start: string | null;
  processing_period_end: string | null;
  source: MeetingProcessingSource;
  event_type: typeof MEETING_PROCESSING_EVENT_TYPE;
}

export interface MeetingProcessingCreditPackRow {
  id?: string | null;
  quantity_purchased?: number | null;
  quantity_used?: number | null;
  status?: string | null;
}

export function isActiveSoloSubscriptionStatus(
  status: string | null | undefined,
): status is ActiveSoloSubscriptionStatus {
  return Boolean(
    status &&
      ACTIVE_SOLO_SUBSCRIPTION_STATUSES.includes(
        status as ActiveSoloSubscriptionStatus,
      ),
  );
}

function asTime(value: string | null | undefined): number | null {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
}

function isStrictBillingTimestamp(value: unknown): boolean {
  if (typeof value !== "string") return false;
  const match = value.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,6}))?(Z|[+-]\d{2}:\d{2})$/,
  );
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);
  const offsetHour = match[8] === "Z" ? 0 : Number(match[8].slice(1, 3));
  const offsetMinute = match[8] === "Z" ? 0 : Number(match[8].slice(4, 6));
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return year >= 1 &&
    month >= 1 && month <= 12 &&
    day >= 1 && day <= (daysInMonth[month - 1] ?? 0) &&
    hour <= 23 && minute <= 59 && second <= 59 &&
    offsetHour <= 23 && offsetMinute <= 59;
}

export function hasValidOptionalBillingTimestamps(params: {
  currentPeriodEnd?: unknown;
  paymentFailureGraceEndsAt?: unknown;
}): boolean {
  return (
    (params.currentPeriodEnd == null || isStrictBillingTimestamp(params.currentPeriodEnd)) &&
    (params.paymentFailureGraceEndsAt == null ||
      isStrictBillingTimestamp(params.paymentFailureGraceEndsAt))
  );
}

export function isSoloPaymentFailureInGrace(params: {
  status?: string | null;
  paymentFailureGraceEndsAt?: string | null;
  now?: Date;
}): boolean {
  if (params.status !== "past_due" && params.status !== "unpaid") return false;
  const graceEnd = asTime(params.paymentFailureGraceEndsAt);
  if (!graceEnd) return false;
  return graceEnd > (params.now ?? new Date()).getTime();
}

export function isSoloCancelingAtPeriodEnd(params: {
  status?: string | null;
  cancelAtPeriodEnd?: boolean | null;
  currentPeriodEnd?: string | null;
  now?: Date;
}): boolean {
  if (!params.cancelAtPeriodEnd) return false;
  if (params.status !== "active" && params.status !== "trialing") return false;
  const periodEnd = asTime(params.currentPeriodEnd);
  if (!periodEnd) return false;
  return periodEnd > (params.now ?? new Date()).getTime();
}

export function isSoloSubscriptionEnded(params: {
  status?: string | null;
  currentPeriodEnd?: string | null;
  now?: Date;
}): boolean {
  if (params.status !== "canceled" && params.status !== "paused") return false;
  const periodEnd = asTime(params.currentPeriodEnd);
  return !periodEnd || periodEnd <= (params.now ?? new Date()).getTime();
}

export function hasSoloLeaderPaidAccess(params: {
  status?: string | null;
  currentPeriodEnd?: string | null;
  cancelAtPeriodEnd?: boolean | null;
  paymentFailureGraceEndsAt?: string | null;
  now?: Date;
}): boolean {
  if (params.status === "active" || params.status === "trialing") {
    const periodEnd = asTime(params.currentPeriodEnd);
    return !periodEnd || periodEnd > (params.now ?? new Date()).getTime();
  }

  if (params.status === "canceled") {
    const periodEnd = asTime(params.currentPeriodEnd);
    return Boolean(periodEnd && periodEnd > (params.now ?? new Date()).getTime());
  }

  if (params.status === "past_due" || params.status === "unpaid") {
    return isSoloPaymentFailureInGrace(params);
  }

  return false;
}

export const hasCoachBillingPaidAccess = hasSoloLeaderPaidAccess;

export function hasManualUnlimitedProcessingAccess(params: {
  status?: string | null;
  promoCodeUsed?: string | null;
}): boolean {
  const promoCodeUsed = params.promoCodeUsed?.trim() ?? null;
  return (
    promoCodeUsed === MANUAL_UNLIMITED_ACCESS_PROMO_CODE &&
    (params.status === "active" || params.status === "trialing")
  );
}

function addUtcMonthsClamped(anchor: Date, monthsToAdd: number): Date {
  const targetMonthStart = new Date(Date.UTC(
    anchor.getUTCFullYear(),
    anchor.getUTCMonth() + monthsToAdd,
    1,
    anchor.getUTCHours(),
    anchor.getUTCMinutes(),
    anchor.getUTCSeconds(),
    anchor.getUTCMilliseconds(),
  ));
  const lastDayOfTargetMonth = new Date(Date.UTC(
    targetMonthStart.getUTCFullYear(),
    targetMonthStart.getUTCMonth() + 1,
    0,
  )).getUTCDate();
  targetMonthStart.setUTCDate(Math.min(anchor.getUTCDate(), lastDayOfTargetMonth));
  return targetMonthStart;
}

export function resolveLeaderBillingPeriod(params: {
  anchor: string | null | undefined;
  now?: Date;
}): { periodStart: string | null; periodEnd: string | null } {
  const anchorValue = asTime(params.anchor);
  if (!anchorValue) return { periodStart: null, periodEnd: null };

  const anchor = new Date(anchorValue);
  const now = params.now ?? new Date();
  if (now.getTime() < anchor.getTime()) {
    return {
      periodStart: anchor.toISOString(),
      periodEnd: addUtcMonthsClamped(anchor, 1).toISOString(),
    };
  }

  let monthOffset =
    (now.getUTCFullYear() - anchor.getUTCFullYear()) * 12 +
    (now.getUTCMonth() - anchor.getUTCMonth());
  let periodStart = addUtcMonthsClamped(anchor, monthOffset);
  let periodEnd = addUtcMonthsClamped(anchor, monthOffset + 1);

  while (now.getTime() >= periodEnd.getTime()) {
    monthOffset += 1;
    periodStart = periodEnd;
    periodEnd = addUtcMonthsClamped(anchor, monthOffset + 1);
  }

  while (now.getTime() < periodStart.getTime()) {
    monthOffset -= 1;
    periodEnd = periodStart;
    periodStart = addUtcMonthsClamped(anchor, monthOffset);
  }

  return {
    periodStart: periodStart.toISOString(),
    periodEnd: periodEnd.toISOString(),
  };
}

export function countDistinctProcessedMeetings(
  rows: Array<{ meeting_id?: string | null; id?: string | null }>,
): number {
  const processedMeetingIds = new Set<string>();
  let unlinkedProcessedCount = 0;

  rows.forEach((row) => {
    if (row.meeting_id) {
      processedMeetingIds.add(row.meeting_id);
      return;
    }
    if (row.id) {
      unlinkedProcessedCount += 1;
    }
  });

  return processedMeetingIds.size + unlinkedProcessedCount;
}

export function countMeetingProcessingEvents(rows: Array<{ id?: string | null }>): number {
  return rows.length;
}

export function countRemainingExtraMeetingCredits(rows: MeetingProcessingCreditPackRow[]): number {
  return rows.reduce((total, row) => {
    if (row.status !== "active") return total;
    return total + Math.max(0, (row.quantity_purchased ?? 0) - (row.quantity_used ?? 0));
  }, 0);
}

export function selectMeetingProcessingSource(params: {
  includedUsed: number;
  includedLimit?: number | null;
  extraCreditsRemaining?: number;
}): MeetingProcessingSource | null {
  if (params.includedLimit === null) {
    return MEETING_PROCESSING_SOURCE_INCLUDED;
  }

  if (params.includedUsed < (params.includedLimit ?? SOLO_LEADER_PAID_PROCESSED_MEETING_LIMIT)) {
    return MEETING_PROCESSING_SOURCE_INCLUDED;
  }

  if ((params.extraCreditsRemaining ?? 0) > 0) {
    return MEETING_PROCESSING_SOURCE_EXTRA_PACK;
  }

  return null;
}

export function buildMeetingProcessingEventPayload(params: {
  leaderId: string;
  meetingId?: string | null;
  transcriptUploadId: string;
  billingOwnerType?: "solo_leader" | "coach" | "org";
  billingOwnerId?: string | null;
  processingPeriodStart?: string | null;
  processingPeriodEnd?: string | null;
  source: MeetingProcessingSource;
}): MeetingProcessingEventPayload {
  return {
    leader_id: params.leaderId,
    meeting_id: params.meetingId ?? null,
    transcript_upload_id: params.transcriptUploadId,
    billing_owner_type: params.billingOwnerType ?? "solo_leader",
    billing_owner_id: params.billingOwnerId ?? params.leaderId,
    processing_period_start: params.processingPeriodStart ?? null,
    processing_period_end: params.processingPeriodEnd ?? null,
    source: params.source,
    event_type: MEETING_PROCESSING_EVENT_TYPE,
  };
}

export function buildAnalyzedStatusFilter(): string[] {
  return [...SOLO_LEADER_ANALYZED_STATUS_ALIASES];
}

export function buildSoloTrialExpiredMessage(): string {
  return "Your Solo Mode trial has ended. You can still revisit your Leadership Record. Upgrade to continue meeting processing.";
}

export function buildSoloPaidLimitMessage(): string {
  return "You've used the meeting processing included in this billing month. You can keep capturing reflections and revisiting your Leadership Record. To process more meetings before your next reset, add 5 more meeting processes.";
}

export function buildExtraMeetingPackLimitMessage(): string {
  return "You've used the meeting processing included in this billing month and do not have extra meeting credits remaining. You can keep capturing reflections and revisiting your Leadership Record.";
}
